import { z } from 'zod'

export const timespan = z.object({
  start: z.string().datetime().or(z.date()),
  end: z.string().datetime().or(z.date()),
  timezone: z.string().default('America/Chicago'),
})

const series = z.enum(['day', 'week', 'month', 'year', 'hour'])

export const column = z.enum([
  'topicIds',
  'agentOvertalkOccurrences',
  'agentOvertalkTime',
  'agentTalkTime',
  'averageDeadAirTime',
  'channelId',
  'workspaceId',
  'groupIds',
  'assignedSupervisorId',
  'contactLogId',
  'customerTalkTime',
  'duration',
  'handleTime',
  'recordingDuration',
  'handledBy',
  'handledByIds',
  'id',
  'isOutbound',
  'method',
  'piiIds',
  'to',
  'workflowId',

  'metHandleTimeGoal',
  'metDeadAirGoal',

  'overallAgentSentimentScore',
  'overallCustomerSentimentScore',
  'initialCustomerSentimentScore',
  'medialCustomerSentimentScore',
  'closingCustomerSentimentScore',

  'overallCustomerSentimentLabelId',
  'initialCustomerSentimentLabelId',
  'initialCustomerReactionIds',
  'medialCustomerSentimentLabelId',
  'medialCustomerReactionIds',
  'closingCustomerSentimentLabelId',
  'closingCustomerReactionIds',
  'sentimentInfluence',

  'customerWordCount',
  'agentWordCount',
  'totalWordCount',

  'intentSummary',
  'intentId',
  'intentDetailId',

  'totalDeadAirTime',
  'deadAirCount',
  'createdAt',
  'startedAt',
  'from',
  'totalHolds',
  'totalHoldTime',
  'talkTime',
  'wrapTime',
  'tagIds',
  'detectedHighlightIds',
  'topicIds',
  'summary',
  'importedAt',
  'externalId',

  'averageEvaluationScore',
  'hasAutoEvaluation',
  'hasManualEvaluation',
  'hasDispute',
  'metQualityGoal',
  'hasEvaluation',

  /* Evaluations Fields */
  'publishedAt',
  'auto',
  'metGoal',
  'score',
  'scorecardId',
  'scorecardCriteriaId',
  'awardedPoints',
  'reviewedAt',

  'referenceNumber',
  'productsOrServices',
  'resolution',
  'flagId',
  'lastCommentedAt',
  'comments',
  'messageCount',
  'customer.name',
  'assignedTo',

  'v2_overallSentimentId',
  'v2_overallSentimentReaction',
  'v2_initialSentimentId',
  'v2_initialSentimentReaction',
  'v2_closingSentimentId',
  'v2_closingSentimentReaction',
  'isVoicemail',

  /* Clustering Fields */
  'signalIds',
  'outcomeId',
  'missedOpportunityIds',
  'qualifiedLead',

  /* Escalation Fields */
  'hasEscalation',
  'escalationRiskScore',
])

export const datasets = z.enum([
  'interactions',
  'evaluations',
  'evaluationCriteriaScores',
  'scorecards',
])

export const field = z.object({
  field: column,
  alias: z.string().optional(),
  hydrate: z.boolean().optional(),
  dataset: datasets.optional(),
})

export const pivotField = field.or(
  z.object({
    field: z.literal('time'),
    groupBy: series,
  }),
)

export const multiValueFilter = z.object({
  field: column,
  op: z.enum(['in', 'nin', 'between']),
  values: z.string().or(z.boolean()).array(),
  dataset: datasets.optional(),
})

export const filter = z
  .object({
    field: column,
    op: z.enum(['eq', 'ne', 'gt', 'lt', 'gte', 'lte', 'isNotNull']),
    value: z.string().or(z.boolean()),
    dataset: datasets.optional(),
  })
  .or(multiValueFilter)
  .or(
    z.object({
      field: column,
      op: z.enum(['isNotNull', 'isNull']),
      dataset: datasets.optional(),
    }),
  )
  .or(
    z.object({
      field: z.literal('summary'),
      op: z.enum(['similarity']),
      value: z.string(),
      threshold: z.number().default(0.75).optional(),
      dataset: datasets.optional(),
      embeddings: z.any().optional(),
    }),
  )

export const getValueAsArray = (f?: z.infer<typeof filter>) => {
  if (!f) return []
  if ('value' in f) return [f.value]
  if ('values' in f) return f.values
  return []
}

const havingSchema = z
  .object({
    op: z.enum(['eq', 'ne', 'gt', 'lt', 'gte', 'lte', 'in', 'nin', 'between']),
    value: z.number().or(z.string()),
  })
  .optional()

export const aggregateCountIf = z.object({
  op: z.literal('countIf'),
  conditions: filter.array(),
  alias: z.string().optional(),
  dataset: datasets.optional(),
})

export const aggregatePercentOf = z.object({
  op: z.literal('percentOf'),
  field: column,
  filter: z.array(filter).optional(),
  alias: z.string().optional(),
  dataset: datasets.optional(),
  scope: z.enum(['group', 'total']),
})

export const aggregate = z
  .object({
    op: z.enum([
      'count',
      'sum',
      'avg',
      'min',
      'max',
      'countDistinct',
      'stddev',
      'sni',
      'p25',
      'p75',
    ]),
    //z.enum(Object.keys(interactions))
    field: column,
    alias: z.string().optional(),
    parent: z.string().optional(),
    precision: z.number().min(0).max(4).default(4),
    trends: z.boolean().optional(),
    dataset: datasets.optional(),
    filter: z.array(filter).optional(),
    having: havingSchema,
    return: z.boolean().optional().default(true),
  })
  .or(aggregateCountIf)
  .or(aggregatePercentOf)

export const aggregations = aggregate.or(field)

export const orderBy = z.object({
  alias: z.string(),
  direction: z.enum(['asc', 'desc']).default('asc'),
})

const baseQuery = z.object({
  dataset: datasets.optional().default('interactions'),
  timespan: timespan,
  aggregations: z.array(aggregations),
  filters: z.array(filter).optional(),
  orderBy: z.array(orderBy).optional(),
  limit: z.number().optional(),
  series: series.optional(),
})

export const lookupQuery = baseQuery.extend({
  alias: z.string(),
  on: z.object({
    local: z.string(),
    parent: z.string(),
  }),
  lookup: z.array(z.string()),
})

const lookup = z.array(z.union([z.string(), lookupQuery]))

export const analyticsQuery = baseQuery.extend({
  lookup: lookup.optional(),
})

export const lookupFields = z.enum([
  'id',
  'customerSentiment',
  'initialCustomerSentiment',
  'medialCustomerSentiment',
  'closingCustomerSentiment',
  'sentimentInfluence',
  'initialCustomerReactions',
  'medialCustomerReactions',
  'closingCustomerReactions',
  'summary',
  'topics',
  'intent',
  'intentSummary',
  'highlights',
  'detectedHighlights',
  'channel',
  'duration',
  'from',
  'to',
  'handledBy',
  'handledByIds',
  'isOutbound',
  'isVoicemail',
  'method',
  'startedAt',
  'totalHolds',
  'totalHoldTime',
  'wrapTime',
  'talkTime',
  'externalId',
  'importedAt',
  'scorecardId',
  'scorecardCriteriaId',
  'workspace',
  'referenceNumber',
  'productsOrServices',
  'resolution',
  'averageEvaluationScore',
  'hasDispute',
  'lastCommentedAt',
  'comments',
  'messageCount',
  'transcript',
  'customer.name',
  'flag',
  'contactLogId',

  /* Clustering Fields */
  'signals',
  'outcome',
  'missedOpportunities',
  'qualifiedLead',

  /* Escalation Fields */
  'hasEscalation',
  'escalationRiskScore',
])

const fieldDefDatatype = z.enum([
  'label',
  'labels',
  'string',
  'datetime',
  'percent',
  'boolean',
  'channel',
  'user',
  'duration',
  'number',
  'tags',
  'highlights',
  'trend',
  'sentiment',
  'scorecards',
  'scorecardCriteria',
  'workspace',
  'comments',
  'flag',
  'transcript',
])

export type FieldDefDatatype = z.infer<typeof fieldDefDatatype>
export type LookupFields = z.infer<typeof lookupFields>
export const LOOKUP_FIELD_DEFS: Record<
  z.infer<typeof lookupFields>,
  {
    label: string
    datatype: FieldDefDatatype
    field: z.infer<typeof column>
    includes?: Record<
      string,
      {
        field: z.infer<typeof column>
        datatype: FieldDefDatatype
      }
    >
    sortable: boolean
    hydrate?: boolean
  }
> = {
  /** Sentiment */

  'customerSentiment': {
    datatype: 'sentiment',
    field: 'overallCustomerSentimentScore',
    label: 'Customer Sentiment',
    sortable: true,
    includes: {
      value: { field: 'overallCustomerSentimentScore', datatype: 'number' },
      label: { field: 'overallCustomerSentimentLabelId', datatype: 'label' },
    },
  },

  'initialCustomerSentiment': {
    datatype: 'sentiment',
    field: 'initialCustomerSentimentScore',
    label: 'Customer Sentiment - Initial',
    sortable: true,
    includes: {
      value: { field: 'initialCustomerSentimentScore', datatype: 'number' },
      label: { field: 'initialCustomerSentimentLabelId', datatype: 'label' },
    },
  },

  'medialCustomerSentiment': {
    datatype: 'sentiment',
    field: 'medialCustomerSentimentScore',
    label: 'Customer Sentiment - Medial',
    sortable: true,
    includes: {
      value: { field: 'medialCustomerSentimentScore', datatype: 'number' },
      label: { field: 'medialCustomerSentimentLabelId', datatype: 'label' },
    },
  },

  'closingCustomerSentiment': {
    datatype: 'sentiment',
    field: 'closingCustomerSentimentScore',
    label: 'Customer Sentiment - Closing',
    sortable: true,
    includes: {
      value: { field: 'closingCustomerSentimentScore', datatype: 'number' },
      label: { field: 'closingCustomerSentimentLabelId', datatype: 'label' },
    },
  },

  'sentimentInfluence': {
    field: 'sentimentInfluence',
    label: 'Sentiment Influence',
    sortable: true,
    datatype: 'trend',
  },

  /** Customer Reactions */

  'initialCustomerReactions': {
    datatype: 'labels',
    label: 'Customer Reactions - Initial',
    field: 'initialCustomerReactionIds',
    sortable: false,
  },

  'medialCustomerReactions': {
    datatype: 'labels',
    label: 'Customer Reactions - Medial',
    field: 'medialCustomerReactionIds',
    sortable: false,
  },

  'closingCustomerReactions': {
    datatype: 'labels',
    label: 'Customer Reactions - Closing',
    field: 'closingCustomerReactionIds',
    sortable: false,
  },

  /** Summary */

  'summary': {
    label: 'Summary',
    field: 'summary',
    datatype: 'string',
    sortable: false,
  },

  'topics': {
    label: 'Topics',
    datatype: 'labels',
    field: 'topicIds',
    sortable: false,
  },

  'intentSummary': {
    label: 'Customer Intent Summary',
    field: 'intentSummary',
    datatype: 'string',
    sortable: true,
  },

  'intent': {
    label: 'Customer Intent',
    datatype: 'label',
    field: 'intentId',
    sortable: true,
  },

  /** Clustering Fields */
  'signals': {
    label: 'Signals',
    datatype: 'labels',
    field: 'signalIds',
    sortable: false,
  },

  'outcome': {
    label: 'Outcome',
    datatype: 'label',
    field: 'outcomeId',
    sortable: true,
    hydrate: true,
  },

  'missedOpportunities': {
    label: 'Missed Opportunities',
    datatype: 'labels',
    field: 'missedOpportunityIds',
    sortable: false,
  },

  'qualifiedLead': {
    label: 'Qualified Lead',
    datatype: 'boolean',
    field: 'qualifiedLead',
    sortable: true,
  },

  'highlights': {
    field: 'tagIds',
    label: 'Highlights',
    datatype: 'tags',
    sortable: false,
  },

  'detectedHighlights': {
    field: 'detectedHighlightIds',
    label: 'Detected Highlights',
    datatype: 'highlights',
    sortable: false,
  },

  /** Evaluation */
  'averageEvaluationScore': {
    field: 'averageEvaluationScore',
    label: 'Evaluation Score',
    datatype: 'percent',
    sortable: true,
  },

  'hasDispute': {
    field: 'hasDispute',
    label: 'Has Dispute',
    datatype: 'boolean',
    sortable: true,
  },

  /* Escalation Fields */
  'hasEscalation': {
    field: 'hasEscalation',
    label: 'Has Escalation',
    datatype: 'boolean',
    sortable: true,
  },

  'escalationRiskScore': {
    field: 'escalationRiskScore',
    label: 'Escalation Risk',
    datatype: 'percent',
    sortable: true,
  },

  /** Phone Status */

  'channel': {
    field: 'channelId',
    label: 'Channel',
    sortable: false,
    datatype: 'channel',
    hydrate: true,
  },

  'workspace': {
    field: 'workspaceId',
    label: 'Workspace',
    sortable: false,
    datatype: 'workspace',
    hydrate: true,
  },

  'duration': {
    field: 'handleTime',
    label: 'Duration',
    sortable: true,
    datatype: 'duration',
  },

  'from': {
    field: 'from',
    label: 'Customer #',
    datatype: 'string',
    sortable: true,
  },

  'to': {
    field: 'to',
    label: 'To address',
    datatype: 'string',
    sortable: true,
  },

  'handledBy': {
    field: 'handledBy',
    label: 'Handled By',
    sortable: false,
    datatype: 'user',
    hydrate: true,
  },

  'handledByIds': {
    field: 'handledByIds',
    label: 'Handled By',
    sortable: false,
    datatype: 'user',
    hydrate: true,
  },

  'isOutbound': {
    field: 'isOutbound',
    label: 'Is Outbound',
    datatype: 'boolean',
    sortable: true,
  },

  'isVoicemail': {
    field: 'isVoicemail',
    label: 'Is Voicemail',
    datatype: 'boolean',
    sortable: true,
  },

  'method': {
    field: 'method',
    label: 'Contact Method',
    datatype: 'string',
    sortable: true,
  },

  'startedAt': {
    field: 'startedAt',
    label: 'Started At',
    datatype: 'datetime',
    sortable: true,
  },

  'totalHolds': {
    field: 'totalHolds',
    label: 'Total Holds',
    datatype: 'number',
    sortable: true,
  },

  'totalHoldTime': {
    field: 'totalHoldTime',
    label: 'Total Hold Time',
    datatype: 'duration',
    sortable: true,
  },

  'wrapTime': {
    field: 'wrapTime',
    label: 'Wrap Time',
    datatype: 'duration',
    sortable: true,
  },

  'talkTime': {
    field: 'talkTime',
    label: 'Talk Time',
    datatype: 'duration',
    sortable: true,
  },

  'flag': {
    field: 'flagId',
    label: 'Flag',
    sortable: false,
    datatype: 'flag',
  },

  /** System */

  'id': {
    field: 'id',
    label: 'Interaction ID',
    datatype: 'string',
    sortable: false,
  },

  'contactLogId': {
    field: 'contactLogId',
    label: 'Interaction ID',
    datatype: 'string',
    sortable: false,
  },

  'externalId': {
    label: 'External ID',
    field: 'externalId',
    datatype: 'string',
    sortable: true,
  },

  'importedAt': {
    label: 'Imported At',
    field: 'importedAt',
    datatype: 'datetime',
    sortable: true,
  },

  /* Evaluations Fields */
  'scorecardId': {
    label: 'Scorecard',
    field: 'scorecardId',
    sortable: false,
    datatype: 'scorecards',
    hydrate: true,
  },

  'scorecardCriteriaId': {
    label: 'Criteria',
    field: 'scorecardCriteriaId',
    sortable: false,
    datatype: 'scorecardCriteria',
    hydrate: true,
  },

  // @ts-ignore
  'channelId': {
    field: 'channelId',
    label: 'Channel',
    sortable: false,
    datatype: 'channel',
    hydrate: true,
  },

  'intentId': {
    field: 'intentId',
    label: 'Intent',
    sortable: false,
    datatype: 'label',
    hydrate: true,
  },

  'tagIds': {
    field: 'tagIds',
    label: 'Highlights',
    sortable: false,
    datatype: 'tagIds',
    hydrate: true,
  },

  'referenceNumber': {
    field: 'referenceNumber',
    label: 'Reference Number',
    sortable: true,
    datatype: 'string',
  },

  'productsOrServices': {
    field: 'productsOrServices',
    label: 'Product or Service',
    sortable: true,
    datatype: 'string',
  },

  'resolution': {
    field: 'resolution',
    label: 'Resolution',
    sortable: true,
    datatype: 'string',
  },

  'lastCommentedAt': {
    field: 'lastCommentedAt',
    label: 'Last Commented At',
    sortable: true,
    datatype: 'datetime',
  },

  'comments': {
    field: 'comments',
    label: 'Comments',
    sortable: false,
    datatype: 'comments',
  },

  'messageCount': {
    field: 'messageCount',
    label: 'Messages',
    sortable: true,
    datatype: 'number',
  },

  'transcript': {
    field: 'messageCount',
    label: 'Transcript',
    sortable: true,
    datatype: 'transcript',
  },

  'customer.name': {
    label: 'Customer Name',
    field: 'customer.name',
    sortable: false,
    datatype: 'string',
    hydrate: true,
  },

  /* Sentiment V2 */

  'v2_initialSentimentId': {
    field: 'v2_initialSentimentId',
    label: 'Initial Sentiment (BETA)',
    sortable: true,
    datatype: 'sentiment',
    includes: {
      label: { field: 'v2_initialSentimentId', datatype: 'label' },
    },
  },

  'v2_initialSentimentReaction': {
    field: 'v2_initialSentimentReaction',
    label: 'Initial Sentiment Reaction (BETA)',
    sortable: true,
    datatype: 'label',
  },

  'v2_overallSentimentId': {
    field: 'v2_overallSentimentId',
    label: 'Overall Sentiment (BETA)',
    sortable: true,
    datatype: 'sentiment',
    includes: {
      label: { field: 'v2_overallSentimentId', datatype: 'label' },
    },
  },

  'v2_overallSentimentReaction': {
    field: 'v2_overallSentimentReaction',
    label: 'Overall Sentiment Reaction (BETA)',
    sortable: true,
    datatype: 'label',
  },

  'v2_closingSentimentId': {
    field: 'v2_closingSentimentId',
    label: 'Closing Sentiment (BETA)',
    sortable: true,
    datatype: 'sentiment',
    includes: {
      label: { field: 'v2_closingSentimentId', datatype: 'label' },
    },
  },

  'v2_closingSentimentReaction': {
    field: 'v2_closingSentimentReaction',
    label: 'Closing Sentiment Reaction (BETA)',
    sortable: true,
    datatype: 'label',
  },
} as const

export const LOOKUP_FIELD_DEF_ARRAY = Object.values(LOOKUP_FIELD_DEFS)
export const LOOKUP_FIELD_DEF_KEYS: Array<keyof typeof LOOKUP_FIELD_DEFS> = [
  'customerSentiment',
  'initialCustomerSentiment',
  'medialCustomerSentiment',
  'closingCustomerSentiment',

  'sentimentInfluence',
  'initialCustomerReactions',
  'medialCustomerReactions',
  'closingCustomerReactions',
  'summary',
  'topics',
  'intent',
  'intentSummary',
  'highlights',
  'detectedHighlights',
  'channel',
  'duration',
  'from',
  'to',
  'handledByIds',
  'isOutbound',
  'isVoicemail',
  'method',
  'startedAt',
  'totalHolds',
  'totalHoldTime',
  'talkTime',
  'wrapTime',
  'externalId',
  'importedAt',
  'referenceNumber',
  'productsOrServices',
  'resolution',
  'averageEvaluationScore',
  'hasDispute',
  'lastCommentedAt',
  'comments',
  'messageCount',
  'transcript',
  'customer.name',
  'flag',
  'contactLogId',

  /* Clustering Fields */
  'signals',
  'outcome',
  'missedOpportunities',
  'qualifiedLead',

  /* Escalation Fields */
  'hasEscalation',
  'escalationRiskScore',
]

export const lookupDataQuery = z.object({
  dataset: datasets.optional().default('interactions'),
  timespan: timespan,
  filters: z.array(filter).optional(),
  limit: z.number().optional(),
  offset: z.number().optional().default(0),
  fields: z.array(lookupFields).default([]),
  orderBy: z.array(orderBy).optional(),
})
